import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, map, mergeMap, of } from 'rxjs';
import { TournamentsResponse } from '../../../all-tournaments/models/api/responses/tournaments.response';
import { SnackBarType } from '../../../shared/components/snack-bar/snack-bar.component';
import { NOTIFICATIONS } from '../../../shared/constants';
import { BadResponseWithError } from '../../../shared/models/bad-response-with-error.interface';
import { NotificationsService } from '../../../shared/services/notifications.service';
import { CommonSettingsActions } from '../../../shared/store/actions/actions-type.action';
import {
    closeCreateTournamentSecondStepModalAction,
    openSetupTournamentModalAction, openSetupTournamentModalActionV2,
} from '../../../shared/store/actions/commonSettings.action';
import { Tournament } from '../../../tournament/models/tournaments.interface';
import { ProfilePlayerTournamentsService } from '../../services/profile-player-tournaments.service';
import { ProfileTournamentsActions } from '../actions/actions-type.action';

@Injectable()
export class ProfilePlayerTournamentsEffect {
    private actions$ = inject(Actions);
    private profilePlayerTournaments = inject(ProfilePlayerTournamentsService);
    private notificationsService = inject(NotificationsService);

    createTournament$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileTournamentsActions.createTournamentDraft),
            exhaustMap((action) =>
                this.profilePlayerTournaments
                    .createTournamentDraft(action.payload)
                    .pipe(
                        mergeMap((response: Tournament) => {
                            this.notificationsService.openSnackBar({
                                title: NOTIFICATIONS.TOURNAMENT_DRAFT_SAVED,
                                type: SnackBarType.Success,
                            });

                            return [
                                ProfileTournamentsActions.createTournamentDraftSuccess(
                                    {
                                        payload: response,
                                    }
                                ),
                                closeCreateTournamentSecondStepModalAction(),
                                openSetupTournamentModalAction(),
                            ];
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: NOTIFICATIONS.SOMETHING_WENT_WRONG,
                                type: SnackBarType.Error,
                            });
                            return of(
                                ProfileTournamentsActions.createTournamentDraftFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    )
            )
        )
    );
    createTournamentV2$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileTournamentsActions.createTournamentV2Draft),
            exhaustMap((action) =>
                this.profilePlayerTournaments
                    .createTournamentV2Draft(action.payload)
                    .pipe(
                        mergeMap((response: Tournament) => {
                            this.notificationsService.openSnackBar({
                                title: NOTIFICATIONS.TOURNAMENT_DRAFT_SAVED,
                                type: SnackBarType.Success,
                            });

                            return [
                                ProfileTournamentsActions.createTournamentV2DraftSuccess(
                                    {
                                        payload: response,
                                    }
                                ),
                                closeCreateTournamentSecondStepModalAction(),
                                openSetupTournamentModalActionV2(),
                            ];
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: NOTIFICATIONS.SOMETHING_WENT_WRONG,
                                type: SnackBarType.Error,
                            });
                            return of(
                                ProfileTournamentsActions.createTournamentDraftFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    )
            )
        )
    );




    createCustomTournament$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileTournamentsActions.createCustomTournamentDraft),
            exhaustMap((action) =>
                this.profilePlayerTournaments
                    .createCustomTournamentDraft(action.payload)
                    .pipe(
                        mergeMap((response: Tournament) => {
                            this.notificationsService.openSnackBar({
                                title: NOTIFICATIONS.TOURNAMENT_DRAFT_SAVED,
                                type: SnackBarType.Success,
                            });

                            return [
                                ProfileTournamentsActions.createTournamentDraftSuccess(
                                    {
                                        payload: response,
                                    }
                                ),
                                closeCreateTournamentSecondStepModalAction(),
                                openSetupTournamentModalAction(),
                            ];
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: NOTIFICATIONS.SOMETHING_WENT_WRONG,
                                type: SnackBarType.Error,
                            });
                            return of(
                                ProfileTournamentsActions.createTournamentDraftFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    )
            )
        )
    );

    updateDraft$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileTournamentsActions.updateTournamentDraft),
            exhaustMap(
                ({
                    payload,
                    shouldUpdateCurrentTournament,
                    navigateToSettings,
                }) =>
                    this.profilePlayerTournaments
                        .updateTournamentDraft(payload)
                        .pipe(
                            mergeMap((response: Tournament) => {
                                this.notificationsService.openSnackBar({
                                    title:
                                        NOTIFICATIONS.TOURNAMENT_DRAFT_SAVED,
                                    type: SnackBarType.Success,
                                });

                                const sideEffects: any[] = [];
                                // const sideEffects: TypedAction<string>[] = [];

                                if (navigateToSettings) {
                                    sideEffects.push(
                                        CommonSettingsActions.navigateToTournamentSettings(
                                            {
                                                payload: {
                                                    tournamentId: response.id,
                                                },
                                            }
                                        )
                                    );
                                }

                                return [
                                    ProfileTournamentsActions.updateTournamentDraftSuccess(
                                        {
                                            payload: response,
                                            shouldUpdateCurrentTournament,
                                        }
                                    ),
                                    ...sideEffects,
                                ];
                            }),
                            catchError((response: BadResponseWithError) => {
                                this.notificationsService.openSnackBar({
                                    title: 'Error',
                                    message: response.error.message,
                                    type: SnackBarType.Error,
                                    error: response.error,
                                });
                                return of(
                                    ProfileTournamentsActions.updateTournamentDraftFailure(
                                        {
                                            payload: response,
                                            shouldUpdateCurrentTournament,
                                        }
                                    )
                                );
                            })
                        )
            )
        )
    );
    updateDraftV2$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileTournamentsActions.updateTournamentV2Draft),
            exhaustMap(
                ({
                    payload,
                    shouldUpdateCurrentTournament,
                    navigateToSettings,
                }) =>
                    this.profilePlayerTournaments
                        .updateTournamentV2Draft(payload)
                        .pipe(
                            mergeMap((response: Tournament) => {
                                this.notificationsService.openSnackBar({
                                    title:
                                        NOTIFICATIONS.TOURNAMENT_DRAFT_SAVED,
                                    type: SnackBarType.Success,
                                });

                                const sideEffects: any[] = [];
                                // const sideEffects: TypedAction<string>[] = [];

                                if (navigateToSettings) {
                                    sideEffects.push(
                                        CommonSettingsActions.navigateToTournamentSettings(
                                            {
                                                payload: {
                                                    tournamentId: response.id,
                                                },
                                            }
                                        )
                                    );
                                }

                                return [
                                    ProfileTournamentsActions.updateTournamentV2DraftSuccess(
                                        {
                                            payload: response,
                                            shouldUpdateCurrentTournament,
                                        }
                                    ),
                                    ...sideEffects,
                                ];
                            }),
                            catchError((response: BadResponseWithError) => {
                                this.notificationsService.openSnackBar({
                                    title: 'Error',
                                    message: response.error.message,
                                    type: SnackBarType.Error,
                                    error: response.error,
                                });
                                return of(
                                    ProfileTournamentsActions.updateTournamentV2DraftFailure(
                                        {
                                            payload: response,
                                            shouldUpdateCurrentTournament,
                                        }
                                    )
                                );
                            })
                        )
            )
        )
    );

    fetchPlayerTournaments$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileTournamentsActions.fetchPlayerTournaments),
            mergeMap((action) =>
                this.profilePlayerTournaments
                    .fetchPlayerTournaments(action.payload)
                    .pipe(
                        map((response: TournamentsResponse) =>
                            ProfileTournamentsActions.fetchPlayerTournamentsSuccess(
                                {
                                    payload: {
                                        tournamentsType: action.payload.type,
                                        response,
                                        patchExistingTournaments:
                                            !!action.payload.patchExistingValue,
                                    },
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                ProfileTournamentsActions.fetchPlayerTournamentsFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );
}
